import { ProductModel as ProductApi } from '@/http/models/Product/Product'
import { ProductVariationModel as ProductVariationApi } from '@/http/models/Product/ProductVariation'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { ProductModel, ProductVariationModel } from '@/types'

import { useFetchProduct } from './useFetchProduct'
import { useProductMetadata } from './useProductMetadata'
import { mapFormToProductBody, mapFormToVariationBody } from './utils'

/** `onSaved` runs after a successful save, before leaving the page (the assistant marks its proposal saved). */
export const useProductForm = (id?: string, options?: { onSaved?: () => void }) => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { product, isLoading, fetchProduct } = useFetchProduct()
  const metadata = useProductMetadata()

  const form = useForm<ProductModel.ProductFormData>({
    defaultValues: {
      name: '',
      slug: '',
      summary: '',
      description: '',
      tags: [],
      deliveryReturnPolicy: '',
      category: '',
      brand: '',
      status: 'active',
      discount: 0,
      discountType: 'cash',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      thumbnailId: '',
      thumbnailUrl: '',
      attributes: [],
      variations: [],
    },
  })

  const [initialVariationIds, setInitialVariationIds] = useState<string[]>([])

  useEffect(() => {
    if (!id) return

    const loadData = async () => {
      const productData = await fetchProduct(id)

      if (!productData) {
        navigate('/products')
        return
      }

      const variationIds = productData.variations?.map((v) => v._id) || []
      setInitialVariationIds(variationIds)

      form.reset({
        name: productData.name || '',
        discount: productData.discount,
        discountType: productData.discount_type,
        description: productData.description || '',
        summary: productData.summary || '',
        tags: productData.tags || [],
        deliveryReturnPolicy: productData.delivery_return_policy || '',
        category: productData.category_id || '',
        brand: productData.brand_id || '',
        status: (productData.status as any) || 'draft',
        metaTitle: productData.meta_title || '',
        metaDescription: productData.meta_description || '',
        metaKeywords: productData.meta_keywords || '',
        slug: productData.slug || '',
        thumbnailId:
          (productData.thumbnail_id?.[0] as any)?._id ||
          (typeof productData.thumbnail_id === 'string' ? productData.thumbnail_id : ''),
        thumbnailUrl: productData.thumbnail_id?.[0]?.path || '',
        attributes:
          (productData as any).attribute_ids ||
          productData.attributes?.map((attr) => attr._id) ||
          [],
        variations:
          productData.variations?.map((v: ProductVariationModel.ProductVariation) => ({
            _id: v._id,
            purchase_cost: v.purchase_cost || 0,
            regular_price: v.regular_price || 0,
            name: v.name || 'Default Variation',
            summary: v.summary || '',
            search_keywords: v.search_keywords || `${productData.name || ''}, ${v.name || ''}`,
            description: v.description || '',
            delivery_return_policy: v.delivery_return_policy || '',
            initial_stock_quantity: v.initial_stock_quantity || 0,
            attribute_value_ids:
              v.attributeValues?.map((av) => av._id) || v.attribute_value_ids || [],
            images:
              v.images?.map((img) => ({
                id: img._id,
                url: img.path,
              })) || [],
          })) || [],
      })
    }

    loadData()
  }, [id, navigate, form, fetchProduct])

  const onSubmit = async (data: ProductModel.ProductFormData) => {
    setIsSubmitting(true)
    try {
      let productId = id

      if (id) {
        const productResponse = await ProductApi.update({
          params: { id },
          body: mapFormToProductBody(data),
        })

        if (!productResponse.success) {
          throw new Error(productResponse.error?.data?.message || 'Failed to update product.')
        }
      } else {
        const productResponse = await ProductApi.create({
          body: mapFormToProductBody(data),
        })

        if (!productResponse.success || !productResponse.data?.result?._id) {
          throw new Error(productResponse.error?.data?.message || 'Failed to create product.')
        }

        productId = productResponse.data.result._id
      }

      if (productId) {
        // Handle variation deletions
        const currentVariationIds = data.variations?.map((v) => v._id).filter(Boolean) as string[]
        const deletedVariationIds = initialVariationIds.filter(
          (id) => !currentVariationIds.includes(id)
        )

        // Delete variations that are no longer present
        if (deletedVariationIds.length > 0) {
          const deletePromises = deletedVariationIds.map((vId) =>
            ProductVariationApi.delete({
              params: { productId: productId!, variationId: vId },
            }).catch((err) => {
              console.error(`Failed to delete variation ${vId}:`, err)
              return { success: false }
            })
          )
          await Promise.all(deletePromises)
        }

        // Create or update variations
        if (data?.variations && data?.variations?.length > 0) {
          const variationPromises = data.variations.map((v) => {
            const variationBody = mapFormToVariationBody(v, productId!)

            if (v?._id) {
              return ProductVariationApi.update({
                params: { productId: productId!, variationId: v._id },
                body: variationBody,
              })
            } else {
              return ProductVariationApi.create({
                params: { productId: productId! },
                body: variationBody,
              })
            }
          })

          const variationResponses = await Promise.all(variationPromises)
          const failedVariations = variationResponses.filter((res) => !res.success)
          if (failedVariations.length > 0) {
            console.error('Some variations failed to save:', failedVariations)
            throw new Error(`Failed to save ${failedVariations.length} variation(s).`)
          }
        }
      }

      toast.success(id ? 'Product updated successfully!' : 'Product created successfully!')
      options?.onSaved?.()
      navigate('/products')
    } catch (error: any) {
      toast.error(error.message || 'An unexpected error occurred.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      const response = await ProductApi.delete({ params: { id } })

      if (!response.success) {
        throw new Error(response.error?.data?.message || 'Failed to delete product')
      }

      toast.success('Product deleted successfully!')
      navigate('/products')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete product')
    }
  }

  return {
    form,
    onSubmit,
    isSubmitting,
    isLoading,
    product,
    fetchProduct,
    ...metadata,
    handleDelete,
  }
}
